import React from "react";
import {
    Box, Typography, Grid, Divider, Paper, Stack,
    Accordion, AccordionSummary, AccordionDetails,
    Tooltip, Chip, Button, LinearProgress
} from "@mui/material";
import {
    ExpandMore, Analytics, Language, ListAlt, Fullscreen, CheckCircle
} from "@mui/icons-material";
import { InvoiceCounter } from "./InvoiceCounter";
import { PayoutDashboardByCurrency, ElementByPayoutStatuses, ElementByErrorTypes } from "../interfaces/PayoutDashboard";
import { Invoice } from "../interfaces/Invoice";
import { ERROR_GLOSSARY, formatPrice, STATUS_THEME } from "./PayoutDashboardHelper";

type CurrencyAccordionProps = {
    row: PayoutDashboardByCurrency,
    onOpenInvoices: (invoices: Invoice[], curr: string) => void
}

export const CurrencyAccordion = ({ row, onOpenInvoices }: CurrencyAccordionProps) => {
    const isAll = row.currency === 'ALL';

    const getStatusData = (status: string): ElementByPayoutStatuses =>
        row.element_by_payout_statuses?.find(s => s.payout_status === status)
        || { payout_status: status, total: 0, percent: 0, amount: 0, percent_amount: 0 };

    const payable = getStatusData('PAYABLE');
    const paidProducer = getStatusData('PAID_PRODUCER');
    const paidOperator = getStatusData('PAID_OPERATOR');

    const hasErrors = row.elements_by_error_types && row.elements_by_error_types.length > 0;

    return (
        <Accordion
            defaultExpanded={isAll}
            sx={{
                mb: isAll ? 4 : 1.5,
                borderRadius: '16px !important',
                boxShadow: isAll ? '0 10px 25px -5px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
                '&:before': { display: 'none' },
                borderLeft: isAll ? '6px solid #0f172a' : '1px solid #e2e8f0',
            }}
        >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ '& .MuiAccordionSummary-content': { m: 0, alignItems: 'center' } }}>
                <Stack direction="row" spacing={3} sx={{ width: '100%', alignItems: 'center', pr: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 160 }}>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: isAll ? '#1e293b' : '#f8fafc', color: isAll ? '#fff' : '#64748b', display: 'flex' }}>
                            {isAll ? <Analytics /> : <Language />}
                        </Box>
                        <Typography sx={{ fontWeight: 800 }}>{isAll ? "GLOBAL VIEW" : row.currency}</Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ flexGrow: 1, justifyContent: 'space-evenly' }}>
                        <InvoiceCounter label="Total" value={row.total_invoice} color="#64748b" />
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                        <InvoiceCounter label="Payable" value={payable.total} color={STATUS_THEME.PAYABLE.color} />
                        <InvoiceCounter label="Paid Prod." value={paidProducer.total} color={STATUS_THEME.PAID_PRODUCER.color} />
                        <InvoiceCounter label="Paid Oper." value={paidOperator.total} color={STATUS_THEME.PAID_OPERATOR.color} />
                    </Stack>

                    <Box sx={{ minWidth: 120, textAlign: 'right' }}>
                        <Chip
                            label={hasErrors ? "PAYABLE" : (payable.total > 0 ? "PENDING" : "OK")}
                            color={hasErrors ? "error" : (payable.total > 0 ? "warning" : "success")}
                            sx={{ fontWeight: 700 }}
                        />
                    </Box>
                </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 3, borderTop: '1px solid #f1f5f9', bgcolor: '#fcfdfe' }}>
                <Grid container spacing={3}>

                    {/* ERRORS */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="overline" sx={{ fontWeight: 900, color: '#64748b', mb: 2, display: 'block' }}>Technical Blockers</Typography>
                        <Stack spacing={1}>
                            {hasErrors
                                ? row.elements_by_error_types.map((err: ElementByErrorTypes) => (
                                    <Tooltip key={err.error_type} title={ERROR_GLOSSARY[err.error_type]} placement="right" arrow>
                                        <Paper variant="outlined" sx={{ p: 1, px: 2, bgcolor: '#fff' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                                        {err.error_type}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#dc2626' }}>
                                                        {err.total}
                                                    </Typography>
                                                </Box>

                                                {!isAll
                                                    && (
                                                        <Typography variant="caption" sx={{ fontWeight: 400, color: '#94a3b8', mt: 0.2 }}>
                                                            {formatPrice(err.amount, row.currency)}
                                                        </Typography>
                                                    )}

                                            </Box>
                                        </Paper>
                                    </Tooltip>
                                ))
                                : <Typography variant="caption" color="textSecondary">No errors detected</Typography>}
                        </Stack>
                    </Grid>

                    {/* STATS */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="overline" sx={{ fontWeight: 900, color: '#64748b', mb: 2, display: 'block' }}>
                            Financial Distribution
                        </Typography>

                        <Stack spacing={2}>
                            {!isAll && (
                                <Box sx={{ p: 1.5 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem' }}>
                                        Total Amount
                                    </Typography>
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#1e293b' }}>
                                        {formatPrice(row.total_amount, row.currency)}
                                    </Typography>
                                </Box>
                            )}

                            {[payable, paidProducer, paidOperator].map((item) => (
                                <Box key={item.payout_status}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: STATUS_THEME[item.payout_status].color }}>
                                            {STATUS_THEME[item.payout_status].label.toUpperCase()}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800 }}>
                                            {isAll ? item.percent : item.percent_amount}%
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={isAll ? item.percent : item.percent_amount}
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            bgcolor: '#f1f5f9',
                                            '& .MuiLinearProgress-bar': { bgcolor: STATUS_THEME[item.payout_status].color }
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600 }}>
                                            {!isAll && formatPrice(item.amount, row.currency)}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                                            {item.total} invoices
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>

                    {/* INVOICES */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2, height: '100%', display: 'flex', flexDirection: 'column',
                                alignItems: 'center', justifyContent: 'center',
                                bgcolor: payable.total > 0 ? '#fff' : '#f0fdf4',
                                borderStyle: payable.total > 0 ? 'solid' : 'dashed',
                                borderColor: payable.total > 0 ? '#e2e8f0' : '#bbf7d0',
                                borderRadius: 4, textAlign: 'center'
                            }}
                        >
                            {payable.total > 0
                                ? (
                                    <>
                                        <ListAlt sx={{ fontSize: 30, color: '#94a3b8', mb: 1 }} />
                                        <Typography variant="caption" color="textSecondary" sx={{ mb: 2, fontWeight: 600 }}>{payable.total} pending in {row.currency}</Typography>
                                        <Button
                                            variant="contained" fullWidth startIcon={<Fullscreen />}
                                            onClick={() => onOpenInvoices(row.invoice_payable || [], row.currency)}
                                            sx={{ borderRadius: 2, fontWeight: 800, bgcolor: '#1e293b', textTransform: 'none' }}
                                        >Review Invoices</Button>
                                    </>
                                )
                                : (
                                    <>
                                        <CheckCircle sx={{ fontSize: 40, color: '#16a34a', mb: 1 }} />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#166534' }}>All Settled</Typography>
                                    </>
                                )}
                        </Paper>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};